import React, { useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useBreadcrumb } from '../contexts/BreadcrumbContext';
import { Cookie } from 'lucide-react';

const SECTIONS = [
  { id: 'what-are-cookies', title: '1. What Are Cookies?' },
  { id: 'how-we-use', title: '2. How We Use Cookies' },
  { id: 'types', title: '3. Types of Cookies We Use' },
  { id: 'third-party', title: '4. Third-Party Cookies' },
  { id: 'managing', title: '5. Managing Your Cookie Preferences' },
  { id: 'do-not-track', title: '6. Do Not Track' },
  { id: 'changes', title: '7. Changes to This Policy' },
  { id: 'contact', title: '8. Contact Us' },
];

const COOKIE_TABLE = [
  {
    category: 'Necessary',
    description: 'Essential for the website to function. Cannot be disabled.',
    examples: ['Session authentication token', 'CSRF protection token', 'Cookie consent preference'],
    canDisable: false,
  },
  {
    category: 'Preferences',
    description: 'Remember your settings and personalize your experience.',
    examples: ['Theme preference (light/dark)', 'Language setting', 'Notification preferences'],
    canDisable: true,
  },
  {
    category: 'Analytics',
    description: 'Help us understand how visitors interact with our platform so we can improve it.',
    examples: ['Google Analytics (page views, sessions)', 'Feature usage tracking', 'Error reporting'],
    canDisable: true,
  },
  {
    category: 'Marketing',
    description: 'Used to deliver relevant advertisements and track campaign performance.',
    examples: ['Ad targeting identifiers', 'Conversion tracking', 'Retargeting pixels'],
    canDisable: true,
  },
];

export default function CookiePolicyPage() {
  const { theme } = useTheme();
  const { setBreadcrumbItems } = useBreadcrumb();

  useEffect(() => {
    setBreadcrumbItems([{ label: 'Cookie Policy', path: '/cookies' }]);
    return () => setBreadcrumbItems([]);
  }, [setBreadcrumbItems]);

  const SectionHeading = ({ id, title }) => (
    <h2 id={id} className={`scroll-mt-28 text-xl font-bold ${theme.text} mt-10 mb-3 pb-2 border-b ${theme.border}`}>
      {title}
    </h2>
  );

  const P = ({ children }) => (
    <p className={`${theme.textSecondary} leading-relaxed mb-4`}>{children}</p>
  );

  const UL = ({ items }) => (
    <ul className={`list-disc list-inside ${theme.textSecondary} space-y-2 mb-4 ml-2`}>
      {items.map((item, i) => <li key={i} className="leading-relaxed">{item}</li>)}
    </ul>
  );

  return (
    <div className={`min-h-screen ${theme.background}`}>
      {/* Header */}
      <div className={`${theme.background} py-4`}>
        <div className="max-w-7xl mx-auto px-4">
          <div className={`${theme.card} border ${theme.border} rounded-xl px-6 py-4 flex items-center gap-3`}>
            <Cookie size={22} className={theme.link} />
            <div>
              <p className={`text-lg font-bold leading-tight ${theme.text}`}>Cookie Policy</p>
              <p className={`text-xs leading-tight ${theme.textSecondary}`}>Last updated: March 20, 2026</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 flex gap-8">
        {/* Sidebar TOC — desktop */}
        <aside className="hidden lg:block w-52 flex-shrink-0">
          <div className={`sticky ${theme.card} border ${theme.border} rounded-xl p-4`} style={{ top: '6rem' }}>
            <p className={`text-xs font-semibold uppercase tracking-wider ${theme.textSecondary} mb-3`}>Contents</p>
            <nav className="space-y-0.5">
              {SECTIONS.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className={`block px-2 py-1.5 rounded-lg text-xs transition-colors no-underline ${theme.textSecondary} ${theme.cardHover}`}
                >
                  {s.title}
                </a>
              ))}
            </nav>
          </div>
        </aside>

        {/* Main content */}
        <div className={`flex-1 min-w-0 ${theme.card} border ${theme.border} rounded-xl p-6 md:p-8`}>
          <P>
            This Cookie Policy explains how Kynaix LLC ("Kynaix," "we," "our," or "us") uses cookies and similar tracking technologies on the Kynaix Sports platform. This policy should be read alongside our <a href="/privacy" className={`${theme.link} underline`}>Privacy Policy</a>.
          </P>

          <SectionHeading id="what-are-cookies" title="1. What Are Cookies?" />
          <P>Cookies are small text files that are placed on your device (computer, smartphone, or tablet) when you visit a website. They are widely used to make websites work more efficiently and to provide information to website owners.</P>
          <P>We also use similar technologies such as local storage, session storage, and pixel tags, which function in a similar way to cookies and are subject to this policy.</P>

          <SectionHeading id="how-we-use" title="2. How We Use Cookies" />
          <P>We use cookies to:</P>
          <UL items={[
            'Keep you signed in to your account across page loads and browser sessions',
            'Remember your preferences such as theme, language, and notification settings',
            'Understand how you use our platform so we can improve it',
            'Measure the effectiveness of features and marketing campaigns',
            'Detect and prevent fraudulent activity and protect your account',
            'Deliver relevant content and advertisements (with your consent)',
          ]} />

          <SectionHeading id="types" title="3. Types of Cookies We Use" />
          <P>We categorize our cookies into four groups. You can manage your preferences for non-essential cookies via our cookie consent banner.</P>

          <div className="space-y-4 mt-4">
            {COOKIE_TABLE.map((row) => (
              <div key={row.category} className={`border ${theme.border} rounded-xl p-4`}>
                <div className="flex items-center justify-between mb-2">
                  <span className={`font-semibold ${theme.text}`}>{row.category}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${row.canDisable
                    ? `bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200`
                    : `bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`}`}>
                    {row.canDisable ? 'Optional' : 'Always Active'}
                  </span>
                </div>
                <p className={`text-sm ${theme.textSecondary} mb-2`}>{row.description}</p>
                <ul className={`text-xs ${theme.textSecondary} space-y-1`}>
                  {row.examples.map((ex, i) => (
                    <li key={i} className="flex items-center gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-current flex-shrink-0 opacity-50" />
                      {ex}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <SectionHeading id="third-party" title="4. Third-Party Cookies" />
          <P>Some cookies on our platform are set by third-party services we use. These include:</P>
          <UL items={[
            'Google Analytics — usage analytics and performance tracking',
            'Stream (GetStream.io) — real-time messaging and activity feeds',
            'AWS CloudFront — content delivery and caching',
            'Payment processors — secure transaction processing (no card data stored by us)',
          ]} />
          <P>These third parties have their own privacy and cookie policies. We recommend reviewing their policies for full details on how they use cookies.</P>

          <SectionHeading id="managing" title="5. Managing Your Cookie Preferences" />
          <P>When you first visit Kynaix Sports, you will be shown a cookie consent banner. You can choose to accept all cookies, customize your preferences by category, or use only necessary cookies.</P>
          <P>You can update your preferences at any time by clicking the "Cookie Settings" link in the footer of our website.</P>
          <P>You can also control cookies through your browser settings:</P>
          <UL items={[
            'Chrome: Settings → Privacy and security → Cookies and other site data',
            'Firefox: Settings → Privacy & Security → Cookies and Site Data',
            'Safari: Preferences → Privacy → Manage Website Data',
            'Edge: Settings → Cookies and site permissions → Cookies and site data',
          ]} />
          <P>Please note that disabling certain cookies may affect the functionality of Kynaix Sports. Necessary cookies cannot be disabled as they are required for the platform to operate.</P>

          <SectionHeading id="do-not-track" title="6. Do Not Track" />
          <P>Some browsers support a "Do Not Track" (DNT) signal. We currently do not respond to DNT signals, as there is no industry-standard protocol for handling them. We encourage you to use our cookie consent tool to manage your preferences directly.</P>

          <SectionHeading id="changes" title="7. Changes to This Policy" />
          <P>We may update this Cookie Policy from time to time to reflect changes in our practices or for legal and regulatory reasons. We will notify you of significant changes by posting a notice on our platform.</P>

          <SectionHeading id="contact" title="8. Contact Us" />
          <P>If you have questions about our use of cookies, please contact us:</P>
          <div className={`${theme.backgroundSecondary} border ${theme.border} rounded-lg p-4 mt-2`}>
            <p className={`font-semibold ${theme.text}`}>Kynaix LLC</p>
            <p className={`${theme.textSecondary} text-sm`}>Email: <a href="mailto:support@kynaixsports.com" className={theme.link}>support@kynaixsports.com</a></p>
            <p className={`${theme.textSecondary} text-sm`}>Website: <a href="https://kynaix.com" className={theme.link}>kynaix.com</a></p>
          </div>

          <div className={`mt-10 pt-6 border-t ${theme.border}`}>
            <p className={`text-xs ${theme.textSecondary} leading-relaxed`}>
              © 2026 Kynaix LLC. All Rights Reserved. All content included on this site is the property of Kynaix LLC or its content suppliers and is protected by United States and international copyright laws.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
